import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useLoading } from "./LoadingContext";

// Animations
const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const LoaderDot = () => {
  const { loading } = useLoading();

  useEffect(() => {
    if (loading) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }

    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [loading]);

  return loading ? (
    <Root>
      <div className="loader-container">
        <div className="loader-content">
          {/* Award Suite Logo */}
          <div className="logo-wrapper">
            <div className="logo-icon">🏅</div>
            <div className="logo-text">
              <span className="award">Award</span>
              <span className="suite">Suite</span>
            </div>
          </div>

          {/* Animated Rings */}
          <div className="rings-container">
            <div className="ring ring-1"></div>
            <div className="ring ring-2"></div>
            <div className="ring ring-3"></div>
          </div>

          {/* Loading Dots */}
          <div className="loading-dots">
            <span className="dot dot-1"></span>
            <span className="dot dot-2"></span>
            <span className="dot dot-3"></span>
          </div>

          {/* Loading Text */}
          <div className="loading-text">
            <span className="text-shimmer">Loading...</span>
          </div>
        </div>
      </div>
    </Root>
  ) : null;
};

export default LoaderDot;

const Root = styled.section`
  .loader-container {
    width: 100vw;
    height: 100vh;
    position: fixed;
    backdrop-filter: blur(8px);
    overflow: hidden !important;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    background: rgba(255, 255, 255, 0.85);
  }

  .loader-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
    animation: ${float} 2s ease-in-out infinite;
  }

  /* Logo Styles */
  .logo-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .logo-icon {
    font-size: 48px;
    animation: ${pulse} 1.5s ease-in-out infinite;
  }

  .logo-text {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
  }

  .award {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a2e;
    letter-spacing: 1px;
  }

  .suite {
    font-size: 28px;
    font-weight: 700;
    background: linear-gradient(135deg, #ff6b35, #f7931e);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  /* Rings Container */
  .rings-container {
    position: relative;
    width: 120px;
    height: 120px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .ring {
    position: absolute;
    border-radius: 50%;
    border: 3px solid transparent;
  }

  .ring-1 {
    width: 120px;
    height: 120px;
    border-top: 3px solid #ff6b35;
    border-right: 3px solid #ff6b35;
    animation: ${spin} 1.2s linear infinite;
  }

  .ring-2 {
    width: 90px;
    height: 90px;
    border-bottom: 3px solid #f7931e;
    border-left: 3px solid #f7931e;
    animation: ${spin} 1.5s linear infinite reverse;
  }

  .ring-3 {
    width: 60px;
    height: 60px;
    border-top: 3px solid #ff6b35;
    border-bottom: 3px solid #f7931e;
    animation: ${spin} 1.8s linear infinite;
  }

  /* Loading Dots */
  .loading-dots {
    display: flex;
    gap: 12px;
    margin-top: -10px;
  }

  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: linear-gradient(135deg, #ff6b35, #f7931e);
    display: inline-block;
  }

  .dot-1 {
    animation: ${pulse} 1s ease-in-out infinite 0s;
  }

  .dot-2 {
    animation: ${pulse} 1s ease-in-out infinite 0.2s;
  }

  .dot-3 {
    animation: ${pulse} 1s ease-in-out infinite 0.4s;
  }

  /* Loading Text */
  .loading-text {
    margin-top: 5px;
  }

  .text-shimmer {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a2e;
    background: linear-gradient(
      90deg,
      #1a1a2e 0%,
      #ff6b35 25%,
      #f7931e 50%,
      #ff6b35 75%,
      #1a1a2e 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 2s linear infinite;
  }
`;